import { OrderItemMetadata } from '@/components/OrderItemMetadata';
import { TravelEsimQrCodeUploader } from '@/components/TravelEsimQrCodeUploader';
import { TravelEsimNumberField } from '@/components/TravelEsimNumberField';
import { TravelEsimPlanLoadedButton } from '@/components/TravelEsimPlanLoadedButton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOrderDetails } from '@/hooks/useOrder';
import { useCompleteOrder, useUpdateTrackingNumber } from '@/hooks/useOrderActions';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, Loader2, Save, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getOrderStatusBadgeClass, getOrderStatusLabel } from '@/lib/order-status';
import { formatPrice } from '@/lib/format';



export function AdminOrderDetailView() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const { mutate: completeOrder } = useCompleteOrder(orderId!);
  const { mutate: updateTrackingNumber, isPending: isUpdatingTracking } =
    useUpdateTrackingNumber(orderId!);

  const { data: order, isLoading, error } = useOrderDetails(orderId!);
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [trackingNumberValue, setTrackingNumberValue] = useState<string>('');

  if (isLoading) {
    return (
      <div className="container py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-8">
        <div className="text-red-500">Error loading order details</div>
      </div>
    );
  }

  const handleCompleteOrder = async () => {
    completeOrder();
  };

  const handleStartEditTracking = () => {
    setTrackingNumberValue(order?.trackingNumber || '');
    setIsEditingTracking(true);
  };

  const handleCancelEditTracking = () => {
    setIsEditingTracking(false);
    setTrackingNumberValue('');
  };

  const handleSaveTracking = () => {
    updateTrackingNumber(trackingNumberValue || null, {
      onSuccess: () => {
        setIsEditingTracking(false);
      },
    });
  };

  // A new eSIM is validated by its QR code sent and its eSIM number; a
  // recharge by the plan confirmed loaded on the existing eSIM.
  const hasUnservedTravelEsim = order.items.some(
    (item) =>
      item.metadata.type === 'TRAVEL_ESIM' &&
      (item.metadata.orderKind === 'RECHARGE'
        ? !item.metadata.planLoadedAt
        : !item.metadata.qrCodeSentAt || !item.metadata.esimNumber),
  );

  return (
    <div className="min-h-screen bg-[#f7f7f8] py-4">
      <div className=" mx-auto">
        {/* Bouton retour */}
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Commande #{order.orderNumber}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground">
                  Organisation:
                </span>
                <span className="text-sm font-semibold text-blue-600">
                  {order.organization.name}
                </span>
              </div>
              <p className="text-muted-foreground mt-2">
                Gérer les détails et le statut de la commande
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge
                className={`${getOrderStatusBadgeClass(
                  order.status
                )} px-3 py-1 text-sm font-semibold`}
              >
                {getOrderStatusLabel(order.status)}
              </Badge>
              {order.status !== 'COMPLETED' && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleCompleteOrder}
                    disabled={order.status === 'COMPLETED' || hasUnservedTravelEsim}
                    title={
                      hasUnservedTravelEsim
                        ? "Chaque eSIM internationale doit avoir son QR code envoyé et son numéro d'eSIM renseigné"
                        : undefined
                    }
                  >
                    Valider
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Infos principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <span className="block text-xs text-muted-foreground">
                  Date
                </span>
                <span className="font-medium">
                  {format(new Date(order.createdAt), 'PPP', { locale: fr })}
                </span>
              </div>
              {order.createdBy && (
                <div>
                  <span className="block text-xs text-muted-foreground">
                    Client
                  </span>
                  <span className="font-medium">
                    {[order.createdBy.firstName, order.createdBy.lastName]
                      .filter(Boolean)
                      .join(' ') || '-'}
                  </span>
                </div>
              )}
              <div>
                <span className="block text-xs text-muted-foreground">
                  Total
                </span>
                <span className="font-medium text-lg">
                  {formatPrice(order.total)}
                </span>
              </div>
              <div>
                <span className="block text-xs text-muted-foreground">
                  Adresse de livraison
                </span>

                <div className="bg-gray-50 rounded-lg p-3 mt-1 text-sm flex flex-col gap-2">
                  <span className="font-medium">{order.organization.name}</span>
                  {order.deliveryAddress && !order.deliveryAddress?.isProfessional && (
                    <Badge variant="outline" className="mb-2">
                      Adresse personnelle
                    </Badge>
                  )}

                  <div className="flex flex-col">
                    {order.deliveryAddress?.isProfessional && (
                      <div>
                        Entreprise:{' '}
                        <span className="font-medium">
                          {order.deliveryAddress?.companyName}
                        </span>
                      </div>
                    )}
                    {order.deliveryAddress?.recipientName && (
                      <div>
                        Destinataire:{' '}
                        <span className="font-medium">
                          {order.deliveryAddress?.recipientName}
                        </span>
                      </div>
                    )}
                    <div>{order.deliveryAddress?.street}</div>
                    <div>
                      {order.deliveryAddress?.zipCode}{' '}
                      {order.deliveryAddress?.city}
                    </div>
                    <div>{order.deliveryAddress?.country}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Articles */}
            <div>
              <span className="block text-xs text-muted-foreground mb-2">
                Articles
              </span>
              <div className="divide-y rounded-lg border bg-gray-50">
                {order.items.map((item) => {
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between px-4 py-3"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-gray-500">
                            {item.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          {item.quantity} ×{' '}
                          {formatPrice(item.price)}
                          {item.isRecurring ? (
                            <span className="text-blue-600 ml-1">/mois</span>
                          ) : (
                            <span className="text-gray-500 ml-1">à l'acte</span>
                          )}
                        </div>
                        {/* Add metadata display */}
                        <OrderItemMetadata
                          item={item}
                          orderId={order.id}
                          canPreviewUnsent
                        />
                        {item.metadata.type === 'TRAVEL_ESIM' &&
                          item.metadata.orderKind !== 'RECHARGE' && (
                            <TravelEsimNumberField
                              orderId={order.id}
                              itemId={item.id}
                              esimNumber={item.metadata.esimNumber}
                            />
                          )}
                      </div>

                      <div className="flex flex-col items-end gap-2 ml-4">
                        <span className="font-semibold text-sm">
                          {formatPrice(item.total)}
                          {item.isRecurring ? (
                            <span className="text-blue-600 ml-1">/mois</span>
                          ) : (
                            <span className="text-gray-500 ml-1">à l'acte</span>
                          )}
                        </span>
                        {item.metadata.type === 'TRAVEL_ESIM' &&
                          (item.metadata.orderKind === 'RECHARGE' ? (
                            <TravelEsimPlanLoadedButton
                              orderId={order.id}
                              itemId={item.id}
                              planLoadedAt={item.metadata.planLoadedAt}
                            />
                          ) : (
                            <TravelEsimQrCodeUploader
                              orderId={order.id}
                              item={item}
                            />
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Numéro de suivi TNT */}
              {order.shippingMethod === 'TNT' && (
                <div className="mt-4">
                  <span className="block text-xs text-muted-foreground mb-2">
                    Numéro de suivi TNT
                  </span>
                  <div className="rounded-lg border bg-gray-50 px-4 py-3">
                    {isEditingTracking && order.status !== 'COMPLETED' ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={trackingNumberValue}
                          onChange={(e) =>
                            setTrackingNumberValue(e.target.value)
                          }
                          placeholder="Entrez le numéro de suivi"
                          className="flex-1"
                          disabled={isUpdatingTracking}
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveTracking}
                          disabled={isUpdatingTracking}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Enregistrer
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEditTracking}
                          disabled={isUpdatingTracking}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Annuler
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {order.trackingNumber || (
                            <span className="text-muted-foreground italic">
                              Non renseigné
                            </span>
                          )}
                        </span>
                        {order.status !== 'COMPLETED' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleStartEditTracking}
                          >
                            {order.trackingNumber ? 'Modifier' : 'Ajouter'}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Totaux */}
            <div className="mt-6 space-y-2">
              {order.fixedTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total HT (achats)
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.fixedTotal - (order.shippingCost ?? 0))}
                  </span>
                </div>
              )}
              {order.recurringTotal > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Total HT/mois (forfaits)
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.recurringTotal)}
                  </span>
                </div>
              )}
              {order.shippingCost && order.shippingCost > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Frais de port
                    {order.shippingMethod === 'LETTRE_SIMPLE'
                      ? ' (Lettre simple)'
                      : order.shippingMethod === 'TNT'
                        ? ' (TNT)'
                        : ''}
                  </span>
                  <span className="font-medium">
                    {formatPrice(order.shippingCost)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between border-t pt-2">
                <span className="font-medium">Total</span>
                <span className="text-lg font-bold">
                  {formatPrice(order.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
